import React from "react";
import CountUp from "react-countup";
import { formatRating } from "../../utils/helpers";

const Ring = ({ value, max = 100, color, size = 140, strokeWidth = 11 }) => {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);
  const offset = circ * (1 - pct);

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#F1F5F9"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1.2s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold text-gray-800">
          <CountUp start={0} end={value} duration={1.2} decimals={value % 1 !== 0 ? 1 : 0} />
          {max === 100 && "%"}
        </span>
      </div>
    </div>
  );
};

const METRICS = [
  {
    key: "completionRate",
    color: "#7EC8A0",
    label: "Job Success Rate",
    sublabel: "Jobs finished",
    bg: "#7EC8A015",
  },
  {
    key: "acceptanceRate",
    color: "#89B4D9",
    label: "Acceptance Rate",
    sublabel: "Offers won",
    bg: "#89B4D915",
  },
  {
    key: "ratingScore",
    color: "#E8A87C",
    label: "Rating Score",
    bg: "#E8A87C15",
  },
  {
    key: "timelinessRate",
    color: "#A78BFA",
    label: "Timeliness Rate",
    sublabel: "On-time check-ins",
    bg: "#A78BFA15",
  },
  {
    key: "cancellationRate",
    color: "#F87171",
    label: "Cancellation Rate",
    sublabel: "Jobs cancelled",
    bg: "#F8717115",
  },
  {
    key: "idleRate",
    color: "#94A3B8",
    label: "Idle Rate",
    sublabel: "Weeks with no jobs (last 12w)",
    bg: "#94A3B815",
  },
];

const EngineerPerformanceRing = ({ summary }) => {
  if (!summary) return null;

  const getValue = (key) => {
    if (key === "ratingScore") return Math.min(Math.round((summary.avgRating ?? 0) * 20), 100);
    return summary[key] ?? 0;
  };

  const getSublabel = (metric) => {
    if (metric.key === "ratingScore") return `${formatRating(summary.avgRating ?? 0)} / 5.00`;
    return metric.sublabel;
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <h4 className="text-sm font-semibold text-gray-700 mb-0.5">Performance Score</h4>
      <p className="text-[11px] text-gray-400 mb-5">Key performance indicators at a glance</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {METRICS.map((metric) => {
          const value = getValue(metric.key);
          const sublabel = getSublabel(metric);
          return (
            <div
              key={metric.key}
              className="flex flex-col items-center text-center gap-3 rounded-xl border border-gray-100 p-5"
              style={{ background: metric.bg }}
            >
              <Ring value={value} color={metric.color} />
              <div className="w-full">
                <p className="text-sm font-bold text-gray-800 leading-snug">{metric.label}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{sublabel}</p>
                <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${value}%`,
                      background: metric.color,
                      transition: "width 1.2s ease",
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EngineerPerformanceRing;
